import React, { useEffect, useMemo, useState, useCallback, useLayoutEffect, Suspense } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Preloader } from 'lib/components/preloader'
import StoreFunctions from 'stores/storeFunctions'
import { useWishlist } from 'utils/use-wishlist'
import { ErrorModal } from 'lib/components/modals/ErrorModal'
import { ConfirmationModal } from 'lib/components/modals/ConfirmationModal'
import { DASOrderSuccessfullModal } from 'lib/components/modals/DASOrderSuccessfullModal'
import { Roles } from 'constants/roles'
import { truncateCurrencyAmount } from 'constants/currencies'
import logger from 'utils/logger'
import { DiamondPageState, ProductDetailsPageProps, RouteParams } from 'types/interfaces'

import { ProductDetailsPage } from '@unbridaled/search-sdk/ProductDetailsPage'
import { MultipleProductDetailsPage } from '@unbridaled/search-sdk/MultipleProductDetailsPage'
import { useAppContext } from 'lib/context/AppContext'
import SimpleStore from 'stores/simpleStore'
import { getSearchSdkLocale } from 'i18n'

export const DiamondPage: React.FC<any> = props => {
  const { diamondStore, appConfig, currencyInfo } = useAppContext()
  const history = useHistory()
  const simpleStore = useMemo(() => new SimpleStore(), [])
  const diamondPreview = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search)
    return searchParams.get('preview') === 'true'
  }, [])
  const storeFunctions = new StoreFunctions()
  const { id } = useParams<RouteParams>()
  const diamondSkus = useMemo(() => id.split("::"), [id])
  const [products, setProducts] = useState<any[]>([])
  const isSingleDiamond = diamondSkus.length == 1
  const [role] = useState(storeFunctions.getUserRole())
  const isCustomer = role === Roles.Customer && !window.location.pathname.includes('merchant/diamond')
  const [isMemoMode] = useState(storeFunctions.isMemoMode())
  const [v360PlayerUrl, setV360PlayerUrl] = useState('')
  const [shopName, setShopName] = useState(null)
  const [shopCurrency, setShopCurrency] = useState<string | null>(null)
  const [pageReady, setPageReady] = useState(false)
  const [config] = useState({
    sourceTypes: appConfig?.source_types,
    advancedSearchCartEnabled: appConfig?.advanced_search_cart_enabled,
    ringSizeRegion: appConfig?.ring_size_region
  })
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [error, setError] = useState<DiamondPageState['error'] | null>(null)
  const [selectedGems, setSelectedGems] = useState<DiamondPageState['selectedGems']>([])
  const [selectedGemsOrderType, setSelectedGemsOrderType] = useState('i')

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [dasOrderSuccessfullModalOpen, setDasOrderSuccessfullModalOpen] = useState(false)

  const normalizeRetailPrice = useCallback((value: unknown): number => {
    const parsed = typeof value === 'string' ? Number(value.replace(/[\,]/g, '')) : Number(value)
    if (!Number.isFinite(parsed)) return 0
    return Math.round(parsed * 100) / 100
  }, [])

  const mapProductForDisplay = useCallback(
    (product: any): any => {
      if (!product) return product

      const isRegularMode = role !== Roles.Merchant && !isMemoMode
      const shopifyRate =
        shopCurrency && currencyInfo.currency !== shopCurrency
          ? window.Shopify?.currency?.rate || 1
          : 1
      const storeRetailPrice = normalizeRetailPrice(product?.retail_price)

      if (isRegularMode) {
        const converted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
        console.log(
          `[price][diamond] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${converted} @ Shopify rate ${shopifyRate}`
        )
        return {
          ...product,
          retail_price: converted,
          currency_code: currencyInfo.currency
        }
      }

      // Merchant/memo (double) mode: keep retail_price in the shop currency for
      // the primary line; the customer-currency (second) line uses the Shopify
      // presentment rate, matching the other surfaces (not DAS's rate).
      const cartConverted = truncateCurrencyAmount(storeRetailPrice * shopifyRate)
      console.log(
        `[price][diamond][double] ${shopCurrency || 'base'} ${storeRetailPrice} -> ${currencyInfo.currency} ${cartConverted} @ Shopify rate ${shopifyRate}`
      )
      return {
        ...product,
        retail_price: storeRetailPrice,
        cart_price: cartConverted,
        currency_code: shopCurrency || product?.currency_code || currencyInfo.currency
      }
    },
    [role, isMemoMode, shopCurrency, currencyInfo.currency, normalizeRetailPrice]
  )

  const mapProductsResponse = useCallback(
    (result: any): any => {
      if (!result) return result
      if (Array.isArray(result?.gems)) {
        return {
          ...result,
          gems: result.gems.map(mapProductForDisplay)
        }
      }
      return mapProductForDisplay(result)
    },
    [mapProductForDisplay]
  )

  const getProducts = async (body: { [key: string]: any }): Promise<void> => {
    const params: { [key: string]: any } = {
      ...body,
      include_unavailable: true,
    }
    params.currency_code = shopCurrency || currencyInfo.currency
    const result = mapProductsResponse(await diamondStore.getDiamond(params, 'search', role))
    return isSingleDiamond ? result : setProducts(result.gems)
  }

  const getMatchedProducts = async (body: { [key: string]: any }): Promise<void> => {
    body.currency_code = shopCurrency || currencyInfo.currency
    return mapProductsResponse(await diamondStore.getDiamond(body, 'matched', role))
  }

  const handleAddToCart = async (gems: { [key: string]: any }[]): Promise<void> => {
    const result = await storeFunctions.addToCart(gems)
    if (!result.success) {
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    window.location.href = '/cart'
  }

  const handleDASOrdering = (diamondsData: Array<any>, orderType: string): void => {
    const gems = Array.isArray(diamondsData) ? diamondsData : [diamondsData]
    setSelectedGems(gems)
    setSelectedGemsOrderType(orderType)
    setConfirmationModalOpen(true)
  }

  const wishlistProps = useWishlist('diamond')

  const componentProps: ProductDetailsPageProps = {
    locale: getSearchSdkLocale(),
    productType: 'diamond',
    role: role,
    isCustomer: isCustomer,
    isActivated: true,
    isOnboarded: true,
    merchant: { company_name: shopName || '', first_name: '' },
    ...(process.env.VIEWER_URL ? { viewerUrl: process.env.VIEWER_URL } : {}),
    v360PlayerUrl: v360PlayerUrl,
    shareProps: { hidden: true },
    nativeV360PlayerUrl: `${location.origin}/apps/ub/360player`,
    currencyProps: {
      mode: role === Roles.Merchant || isMemoMode ? 'double' : 'single',
      currencies: currencyInfo.currencies,
      currencyCode: currencyInfo.currency,
      // Second (customer-currency) line = retail_price x this rate. Use the Shopify
      // presentment rate so it matches checkout/other surfaces (not DAS's rate). In
      // single mode retail_price is already converted, so rate is 1 (no re-multiply).
      currencyRate:
        role === Roles.Merchant || isMemoMode
          ? shopCurrency && currencyInfo.currency !== shopCurrency
            ? window.Shopify?.currency?.rate || 1
            : 1
          : 1
    },
    hideMatchedDiamonds: diamondPreview,
    api: isSingleDiamond
      ? {
          getProducts,
          getMatchedProducts
        }
      : {},
    ...(isSingleDiamond ? { data: { sku: id } } : {}),
    ...(!isSingleDiamond ? { products } : {}),
    ...(!diamondPreview
      ? {
          onBack: (): void => {
            const searchParams = new URLSearchParams(window.location.search)
            searchParams.delete('preview')
            const params = searchParams.toString()
            history.push(
              `/apps/ub/diamonds${params ? `?${params}` : ''}`
            )
          }
        }
      : {}),
    ...(config.advancedSearchCartEnabled && !diamondPreview ? { onAddCartItem: handleAddToCart } : {}),
    ...(isMemoMode && !diamondPreview
      ? {
          onAddInStoreItem: (item: Array<any>): void => handleDASOrdering(item, 'i'),
          onAddMemoItem: (item: Array<any>): void => handleDASOrdering(item, 'm')
        }
      : {}),
    ...(!diamondPreview
      ? {
          onAddJewelryItem: (item: { [key: string]: any }): void => {
            const searchParams = new URLSearchParams(window.location.search)
            const jType = searchParams.get('jewelry_type')
            const jewelryType = jType !== null ? `?jewelry_type=${jType}` : ''
            history.push('/apps/ub/diamond_collection/' + item.sku + jewelryType)
          }
        }
      : {}),
    onDetailsClick: (item: { [key: string]: any }, isCustomerLink: boolean): void => {
      let pathPart = 'merchant/diamond'
      if (
        (isCustomerLink && role === Roles.Merchant) ||
        (!isCustomerLink && role !== Roles.Merchant)
      ) {
        pathPart = 'diamond'
      }

      const searchParams = new URLSearchParams(window.location.search)
      const jType = searchParams.get('jewelry_type')
      const params = new URLSearchParams()

      if (jType !== null) {
        params.set('jewelry_type', jType)
      }

      const query = params.toString()

      window.open(`${window.location.origin}/apps/ub/${pathPart}/${item.sku}${query ? `?${query}` : ''}`, '_blank')
    },
    onJewelryCheck: async (data: { [key: string]: any }): Promise<boolean> => {
      try {
        const productIds = await storeFunctions.tagProductsForSKU([data])
        if (productIds) {
          return true
        }
        return false
      } catch (e) {
        return false
      }
    },
    ringSizeProps: {
      sizeRegion: config.ringSizeRegion
    }
  }

  if (role === Roles.Merchant || isMemoMode) {
    componentProps.wishlistProps = wishlistProps
  }

  const fetchShopName = async (): Promise<void | null> => {
    try {
      const shopInfo = await diamondStore.getShopName()
      setShopName(shopInfo.shopName)
    } catch (e) {
      logger.error('Failed to get shop name')
      return null
    }
  }

  const fetchShopCurrency = async (): Promise<void | null> => {
    try {
      const currencyData = await simpleStore.ajax('GET', 'shop_currency')
      if (currencyData?.currency) {
        setShopCurrency(currencyData.currency)
        return
      }
      throw new Error('Failed to get shop currency')
    } catch (e) {
      logger.error('Failed to get shop currency')
      return null
    }
  }

  useEffect(() => {
    const loadPageData = async (): Promise<void> => {
      await Promise.all([fetchShopName(), fetchShopCurrency()])
      setPageReady(true)
    }

    loadPageData()
  }, [])

  useEffect(() => {
    if (!shopCurrency || isSingleDiamond) return
    getProducts({
      filters: {
        ...{ sku: diamondSkus },
      },
      limit: diamondSkus.length,
    })
  }, [shopCurrency, isSingleDiamond, diamondSkus])

  useLayoutEffect(() => {
    setV360PlayerUrl(`${process.env.VIEWER_URL}/api/360player`)
  }, [])

  const closeErrorModal = (): void => {
    setError(null)
    setErrorModalOpen(false)
  }

  const closeConfirmationModal = (): void => {
    setConfirmationModalOpen(false)
  }

  const closeDASOrderSuccessfullModal = (): void => {
    setDasOrderSuccessfullModalOpen(false)
  }

  const handleAddToDAS = useCallback(async (): Promise<void> => {
    const result = await storeFunctions.addToDAS({
      diamonds: selectedGems,
      type: selectedGemsOrderType,
      shop: shopName || '',
      searchType: "diamond_search"
    })
    if (!result.success) {
      setConfirmationModalOpen(false)
      setError({
        title: result.error?.errorTitle || '',
        message: result.error?.errorBody || ''
      })
      setErrorModalOpen(true)
      return
    }
    setConfirmationModalOpen(false)
    setDasOrderSuccessfullModalOpen(true)
  }, [selectedGems, selectedGemsOrderType, shopName])

  const Component = isSingleDiamond ? ProductDetailsPage : MultipleProductDetailsPage


  return (
    <div
      className={`unbridaled-single-diamond ${diamondPreview ? 'preview' : ''} ${
        role === Roles.Merchant || isMemoMode ? 'memo' : ''
      }`}
    >
      <ErrorModal
        open={errorModalOpen}
        title={error?.title}
        message={error?.message}
        onClose={closeErrorModal}
      />
      <ConfirmationModal
        open={confirmationModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        currencyInfo={currencyInfo}
        onConfirm={handleAddToDAS}
        onClose={closeConfirmationModal}
      />
      <DASOrderSuccessfullModal
        open={dasOrderSuccessfullModalOpen}
        gems={selectedGems}
        orderType={selectedGemsOrderType}
        config={config}
        currencyInfo={currencyInfo}
        onClose={closeDASOrderSuccessfullModal}
      />
      <Suspense fallback={<Preloader preloaderClass="unbridaled-full-page-preloader" />}>
        {pageReady ? <Component {...componentProps} /> : <Preloader preloaderClass="unbridaled-full-page-preloader" />}
      </Suspense>
    </div>
  )
}

export default DiamondPage
